import { configuration } from "@configuration";
import { InlineIcon } from "@iconify/react";
import { useKeycloak } from "@react-keycloak/web";
import { DateTime } from "luxon";
import { memo, useContext } from "react";
import { Link } from "react-router-dom";
import { ASSET_URL } from "../../api";
import { TranslationContext } from "../../contexts";
import { handleNonValidImg } from "../../helpers";
import { useQueryAppConfiguration } from "../../hooks/appConfiguration/useQueryAppConfiguration";
import { useMarketplaceIdentity } from "../../hooks/useMarketplaceIdentity";
import { useQuerySocialMedia } from "../../hooks/socialMedia/useQuerySocialMedia";
import { getLinks } from "./helpers";

const Footer = memo(() => {
  const { t } = useContext(TranslationContext);

  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak.authenticated;
  const { showSellerNav } = useMarketplaceIdentity();

  const { data: configObj, isLoading } = useQueryAppConfiguration();

  const { data: socialMediaData, isLoading: isLoadingSocialMedia } =
    useQuerySocialMedia();

  const loading = isLoading || isLoadingSocialMedia;

  return loading ? null : (
    <>
      {/* Gradient accent bar */}
      <div
        className="h-1 w-full"
        style={{
          background:
            "linear-gradient(90deg, #1E3A8A 0%, #0F766E 50%, #059669 100%)",
        }}
      />
      <footer
        className="relative border-t border-slate-200/60 shadow-[0_-4px_24px_-4px_rgba(15,23,42,0.06)]"
        style={{ background: "rgb(245, 245, 245)" }}
      >
        <div className="mx-auto max-w-6xl px-6 py-12 md:px-8 lg:px-10">
          {/* Main content grid */}
          <nav className="grid gap-10 py-6 md:grid-cols-2 md:gap-12 lg:grid-cols-3 lg:gap-16">
            {/* Quick links section */}
            <div className="flex flex-col items-center gap-4 text-center md:items-start md:text-left">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                Quick Links
              </h3>
              <div className="flex flex-col gap-3">
                {configObj.homePageUrl && (
                  <a
                    href={configObj.homePageUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                    className="text-base font-medium text-slate-700 transition-colors hover:text-teal-600 focus:text-teal-600 focus:outline-none"
                  >
                    {t("homePage")}
                  </a>
                )}
                {getLinks()
                  .filter(
                    (link) => isAuthenticated || link.path === "/products"
                  )
                  .filter(
                    (link) => showSellerNav || link.path !== "/asset-management"
                  )
                  .map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="text-base font-medium text-slate-700 transition-colors hover:text-teal-600 focus:text-teal-600 focus:outline-none"
                      data-testid={
                        link.path === "/products"
                          ? "footerProductsLink"
                          : undefined
                      }
                    >
                      {t(link.name)}
                    </Link>
                  ))}
              </div>
            </div>

            {/* Policy links section */}
            {(configObj.cookieAndStoragePolicyDoc ||
              configObj.dataRetentionAndDeletionPolicyDoc ||
              configObj.privacyPolicyDoc) && (
              <div className="flex flex-col items-center gap-4 text-center md:items-start md:text-left">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                  Policies
                </h3>
                <div className="flex flex-col gap-3">
                  {configObj.cookieAndStoragePolicyDoc && (
                    <a
                      href={configObj.cookieAndStoragePolicyDoc}
                      rel="noopener noreferrer"
                      target="_blank"
                      className="text-base font-medium text-slate-700 transition-colors hover:text-teal-600 focus:text-teal-600 focus:outline-none"
                    >
                      Cookie & Storage Policy
                    </a>
                  )}
                  {configObj.dataRetentionAndDeletionPolicyDoc && (
                    <a
                      href={configObj.dataRetentionAndDeletionPolicyDoc}
                      rel="noopener noreferrer"
                      target="_blank"
                      className="text-base font-medium text-slate-700 transition-colors hover:text-teal-600 focus:text-teal-600 focus:outline-none"
                    >
                      Data Retention & Deletion
                    </a>
                  )}
                  {configObj.privacyPolicyDoc && (
                    <a
                      href={configObj.privacyPolicyDoc}
                      rel="noopener noreferrer"
                      target="_blank"
                      className="text-base font-medium text-slate-700 transition-colors hover:text-teal-600 focus:text-teal-600 focus:outline-none"
                    >
                      Privacy Policy
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Social media section */}
            {Object.keys(socialMediaData).length > 0 && (
              <div className="flex flex-col items-center gap-4 md:items-start">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                  Follow Us
                </h3>
                <div className="flex flex-wrap items-center justify-center gap-4 md:justify-start">
                  {socialMediaData.linkedin && (
                    <a
                      href={socialMediaData.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-teal-300 hover:bg-teal-50 hover:text-teal-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                      aria-label="LinkedIn"
                    >
                      <InlineIcon icon="uiw:linkedin" className="text-lg" />
                    </a>
                  )}
                  {socialMediaData.twitter && (
                    <a
                      href={socialMediaData.twitter}
                      target="_blank"
                      rel="noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-teal-300 hover:bg-teal-50 hover:text-teal-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                      aria-label="Twitter"
                    >
                      <InlineIcon icon="bi:twitter-x" className="text-lg" />
                    </a>
                  )}
                  {socialMediaData.youtube && (
                    <a
                      href={socialMediaData.youtube}
                      target="_blank"
                      rel="noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-teal-300 hover:bg-teal-50 hover:text-teal-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                      aria-label="YouTube"
                    >
                      <InlineIcon icon="bi:youtube" className="text-lg" />
                    </a>
                  )}
                  {socialMediaData.facebook && (
                    <a
                      href={socialMediaData.facebook}
                      target="_blank"
                      rel="noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-teal-300 hover:bg-teal-50 hover:text-teal-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                      aria-label="Facebook"
                    >
                      <InlineIcon
                        icon="akar-icons:facebook-fill"
                        className="text-lg"
                      />
                    </a>
                  )}
                  {socialMediaData.instagram && (
                    <a
                      href={socialMediaData.instagram}
                      target="_blank"
                      rel="noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-all hover:border-teal-300 hover:bg-teal-50 hover:text-teal-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                      aria-label="Instagram"
                    >
                      <InlineIcon
                        icon="akar-icons:instagram-fill"
                        className="text-lg"
                      />
                    </a>
                  )}
                </div>
              </div>
            )}
          </nav>

          {/* EU funding section - kept as requested */}
          <div className="flex flex-col items-center gap-4 border-t border-slate-200/80 pt-10 pb-8">
            {configObj.footerEuFlagImageSrc && (
              <img
                src={`${ASSET_URL}/${configObj.footerEuFlagImageSrc}`}
                alt={configObj.footerEuFlagImageSrcText}
                onError={handleNonValidImg}
                className="h-auto w-56 max-w-[14rem] object-contain drop-shadow-sm md:w-64"
              />
            )}
            {configObj.projectRegistrationNumber && (
              <p className="max-w-2xl text-center text-sm leading-relaxed text-slate-600">
                {t("fundedBy")}
                {configObj.projectRegistrationNumber}.
              </p>
            )}
          </div>

          {/* Copyright & version bar */}
          <div className="flex flex-col items-center justify-between gap-4 border-t border-slate-200/80 py-6 md:flex-row">
            <p className="text-sm text-slate-500">
              {configuration.copyright}
              {DateTime.local().year}
            </p>
            <span
              title={`Version: ${import.meta.env.VITE_APP_VERSION || "dev"}`}
              data-testid="appVersion"
              className="rounded-md bg-slate-100 px-2.5 py-1 font-mono text-xs text-slate-500"
            >
              v{import.meta.env.VITE_APP_VERSION || "dev"}
            </span>
          </div>
        </div>
      </footer>
    </>
  );
});
export default Footer;
